import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";

const API_URL = `${import.meta.env.VITE_API_URL}/partner-charges`;
const makeRequestDebounced = createDebounce(makeRequest, 200);

export type ChargeReference = {
  type: string;
  id: string;
};

export interface IPartnerCharge {
  _id?: string;
  referenceId: ChargeReference[];
  partnerId: string;
  partnerCompanyName: string;
  createdAt: Date;
  status: "OPEN" | "CHARGED";
  notes: string;
  companyId?: string;
  maintenanceCount?: number;
  preparationCount?: number;
  totalValue?: number;
}

export interface IPartnerChargeDetails extends IPartnerCharge {
  detailedReferences: Record<string, unknown>[];
  summary: {
    totalValue: number;
    totalVehicles: number;
    totalServices: number;
    maintenanceCount: number;
  };
}

export async function getChargePaged({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  startDateAtualizadoEm = "",
  endDateAtualizadoEm = "",
  page = 1,
  itemsPerPage = 10,
  orderBy = "",
  technicalConsultant = "",
  fields = "_id, externalId, dueDate, paymentDate, chargeType, clientType, clientId, amount, status, details, notes, createdAt, updatedAt, contest",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  params.append("filters[clientType]", "PARTNER");
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm &&
    params.append(
      "filters[startDateAtualizadoEm]",
      startDateAtualizadoEm.toString()
    );
  endDateAtualizadoEm &&
    params.append(
      "filters[endDateAtualizadoEm]",
      endDateAtualizadoEm.toString()
    );
  technicalConsultant &&
    params.append("filters[technicalConsultant]", technicalConsultant);

  const url = `${API_URL}/charges-paged?${params.toString()}`;
  return (await makeRequestDebounced(url)) as ApiResponseType<unknown>;
}

export const chargesDendDispute = async (
  chargeId: string,
  messageResponse: string
) => {
  const url = `${API_URL}/charges-send-dispute`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      chargeId,
      messageResponse,
    }),
  };

  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<unknown>
  >;
};

export async function generateNf(chargeId: string) {
  const url = `${API_URL}/charge/${chargeId}/generateNf`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  };

  return makeRequest(url, options) as Promise<ApiResponseType<{ nf: string }>>;
}


export async function upsertPartnerCharge(data: {
  referenceId: ChargeReference[];
  licensePlate: string;
  partnerCompanyName: string;
  companyId?: string;
  notes?: string;
}) {
  const url = `${API_URL}/upsert`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequest(url, options) as Promise<ApiResponseType<IPartnerCharge>>;
}

export async function getPartnerCharges(params: Record<string, unknown> = {}) {
  const searchParams = new URLSearchParams(params as Record<string, string>);
  const url = `${API_URL}/?${searchParams.toString()}`;
  return makeRequestDebounced(url) as Promise<
    ApiResponseType<IPartnerCharge[]> & { totalItems: number }
  >;
}

export async function getPartnerChargeById(id: string) {
  const url = `${API_URL}/${id}`;
  return makeRequest(url) as Promise<ApiResponseType<IPartnerChargeDetails>>;
}

export async function chargePartnerCharge(id: string) {
  const url = `${API_URL}/${id}/charge`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  };
  return makeRequest(url, options) as Promise<ApiResponseType<IPartnerCharge>>;
}
